import { useState } from 'react';
import styled from 'styled-components';
import { Spain } from '../components/Icons/Flags/Spain';
import { Sweden } from '../components/Icons/Flags/Sweden';
import { Norway } from '../components/Icons/Flags/Norway';
import { Finland } from '../components/Icons/Flags/Finland';
import { getRolesFromSessionStorage, hasAccessRoles } from '../utils/userHelper';
import { Country } from "../types/common";

const Container = styled.div`
  position: relative;
  width: 100%;
  margin-right: 0.5rem;
`;

const Button = styled.button`
  width: 100%;
  padding: 0.4rem 0.8rem;
  border-radius: 0.375rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  transition: box-shadow 0.2s;
  background-color: #fff;
  border: solid 1px #bbb;
  
  &:hover {
    box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  }
`;

const Dropdown = styled.div`
  position: absolute;
  background-color: #ffffff;
  min-width: 100%;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  border-radius: 0.375rem;
`;

const OptionButton = styled.button<any>`
  padding: 0.5rem;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border:none;
  border-bottom: 1px solid #e2e8f0;
  background-color: ${(props) => (props.selected ? "#ebf8ff" : "transparent")};
  
  &:hover {
    background-color: #ebf8ff;
  }

`;

const OptionLabel = styled.span`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
`;

const IconWrapper = styled.span`
  width: 1rem;
`;

const COUNTRY_CODES = [Country.ES, Country.SE, Country.NO, Country.FI] as const;

const STORAGE_KEYS = {
  COUNTRY: "casemanager:user:country",
} as const;

export function isCountryAccessible(country: string): boolean {
  const userRoles = getRolesFromSessionStorage();

  let hasAccess = false;

  switch (country) {
    case "ES":
      hasAccess = hasAccessRoles(userRoles, [
        "admin-es",
        "caseworker-es",
        "reader-es",
        "admin-all",
        "reader-all",
      ]);
      break;
    case "SE":
      hasAccess = hasAccessRoles(userRoles, [
        "admin-se",
        "caseworker-se",
        "reader-se",
        "admin-all",
        "reader-all",
      ]);
      break;
    case "NO":
      hasAccess = hasAccessRoles(userRoles, [
        "admin-no",
        "caseworker-no",
        "reader-no",
        "admin-all",
        "reader-all",
      ]);
      break;
    case "FI":
      hasAccess = hasAccessRoles(userRoles, ["admin-fi","caseworker-fi","reader-fi", "admin-all", "reader-all"]);
      break;
    default:
      break;
  }
  return hasAccess;
}

export function getSelectedCountry(): string {
  const stored = localStorage.getItem(STORAGE_KEYS.COUNTRY);
  if (stored) return stored;

  const roles = getRolesFromSessionStorage();
  if (roles.length === 0) return COUNTRY_CODES[0];

  const defaultCountry =
    COUNTRY_CODES.find((code) => isCountryAccessible(code)) ?? COUNTRY_CODES[0];
  localStorage.setItem(STORAGE_KEYS.COUNTRY, defaultCountry);
  return defaultCountry;
}

export function CountrySelection() {
  function setCountry(lang: string) {
    localStorage.setItem(STORAGE_KEYS.COUNTRY, lang);
    window.location.reload();
  }

  const [isOpen, setIsOpen] = useState(false);

  const selectedCountry = getSelectedCountry();
  const options = [
    {
      value: "ES",
      label: "Spain",
      icon: <Spain />,
      isSelected: selectedCountry === "ES",
      shouldShow: true,
    },
    {
      value: "SE",
      label: "Sweden",
      icon: <Sweden />,
      isSelected: selectedCountry === "SE",
      shouldShow: true,
    },
    {
      value: "NO",
      label: "Norway",
      icon: <Norway />,
      isSelected: selectedCountry === "NO",
      shouldShow: true,
    },
    {
      value: 'FI',
      label: 'Finland',
      icon: <Finland />,
      isSelected: selectedCountry === 'FI',
      shouldShow: true,
    }
  ];

  const languageMapper = (lang: string) => {
    switch (lang) {
      case "ES":
        return (
          <OptionLabel>
            <IconWrapper>
              <Spain />
            </IconWrapper>
            <div>Spain</div>
          </OptionLabel>
        );
      case "SE":
        return (
          <OptionLabel>
            <IconWrapper>
              <Sweden />
            </IconWrapper>
            Sweden
          </OptionLabel>
        );
      case "NO":
        return (
          <OptionLabel>
            <IconWrapper>
              <Norway />
            </IconWrapper>
            Norway
          </OptionLabel>
        );
      case "FI":
        return (
          <OptionLabel>
            <IconWrapper>
              <Finland />
            </IconWrapper>
            Finland
          </OptionLabel>
        );
      case "dev":
        return "🚀 Developer Mode";
      default:
        return null;
    }
  };

  return (
    <Container onMouseLeave={() => setIsOpen(false)}>
      <Button onClick={() => setIsOpen(!isOpen)}>
        {languageMapper(selectedCountry)}
      </Button>
      {isOpen && (
        <Dropdown>
          {options.map((option) => {
            if (option.shouldShow && isCountryAccessible(option.value))
              return (
                <OptionButton
                  key={option.value}
                  selected={option.isSelected}
                  onClick={() => setCountry(option.value)}
                >
                  <OptionLabel>
                    <IconWrapper>{option.icon}</IconWrapper>
                    {option.label}
                  </OptionLabel>
                </OptionButton>
              );
          })}
        </Dropdown>
      )}
    </Container>
  );
}
